import { object } from '@art-suite/art-core-ts';
import {
  ApiScan,
  ApiUser,
  getProjectActivityLogQueryKeyPrefix,
  getUserByIdQueryKey,
  getUserQueryKeyPrefix,
  getUsersByOrgMarketIdQueryKey,
  getUsersForScanQueryKey,
  getUsersQueryKey,
} from '@kaiizen/kai-pro-api';
import { QueryCache, useCachedQuery, useQueryCache } from '@kaiizen/react-persistent-cache';
import { useMemo, useRef } from 'react';
import { Mutex } from '../lib';
import { useKaiProApiClient } from '../useKaiProApiClient';

const postProcessUsers = <T extends ApiUser>(users: Promise<T[]>, queryCache: QueryCache) =>
  users.then(users => {
    users.forEach(user => {
      queryCache.cache.set(getUserByIdQueryKey(user.id), user);
    });
    return { users, usersById: object(users, { withKey: ({ id }) => id }) };
  });

export const useUserMutators = () => {
  const queryCache = useQueryCache();
  return {
    refetchAllUsers: () => queryCache.refetchAll({ prefix: getUserQueryKeyPrefix() }),
  };
};

const getUsersQueryFunction = <T extends ApiUser>(apiCall: () => Promise<T[]>) => async (key: string, queryCache: QueryCache) =>
  postProcessUsers(apiCall(), queryCache);

export const useUsers = (options?: { search?: string }) => {
  const kaiProApiClient = useKaiProApiClient();

  const query = useCachedQuery(
    getUsersQueryKey(options?.search),
    getUsersQueryFunction(() => kaiProApiClient.getUsers({ search: options?.search })),
    { enabled: !!kaiProApiClient.token },
  );

  return {
    ...query,
    ...query.data,
  };
};

export const useUsersByOrgMarketId = (orgMarketId?: string) => {
  const kaiProApiClient = useKaiProApiClient();
  const query = useCachedQuery(
    getUsersByOrgMarketIdQueryKey(orgMarketId),
    getUsersQueryFunction(() => kaiProApiClient.getUsersByOrgMarketId(orgMarketId!)),
    { enabled: !!kaiProApiClient.token && !!orgMarketId },
  );

  return {
    ...query,
    ...query.data,
  };
};

export type UsersForScanMutators = {
  removeUserFromScan: (userId: string, scanId: string) => Promise<ApiScan>;
  assignUserToScan: (userId: string, scanId: string) => Promise<ApiScan>;
};

export const useUsersForScan = (scanId?: string, options?: { projectId?: string | null }) => {
  const kaiProApiClient = useKaiProApiClient();
  const queryCache = useQueryCache();
  const mutationMutexRef = useRef<Mutex>(new Mutex());

  const query = useCachedQuery(
    getUsersForScanQueryKey(scanId),
    getUsersQueryFunction(() => kaiProApiClient.getUsersForScan(scanId!).then(users => users.map(user => user.user))),
    { enabled: !!kaiProApiClient.token && !!scanId },
  );

  return {
    ...query,
    usersForScan: query.data?.users,
    usersForScanByUserId: query.data?.usersById,
    usersForScanMutators: useMemo(() => ({
      removeUserFromScan: async (userId: string, targetScanId: string) =>
        mutationMutexRef.current.runExclusive(async () => {
          const result = await kaiProApiClient.removeUserFromScan(userId, targetScanId);
          await queryCache.refetchAll({ prefix: getUsersForScanQueryKey(targetScanId) });
          if (options?.projectId) {
            await queryCache.refetchAll({ prefix: getProjectActivityLogQueryKeyPrefix(options.projectId) });
          }
          return result as unknown as ApiScan;
        }),
      assignUserToScan: async (userId: string, targetScanId: string) =>
        mutationMutexRef.current.runExclusive(async () => {
          const result = await kaiProApiClient.assignUserToScan(userId, targetScanId);
          await queryCache.refetchAll({ prefix: getUsersForScanQueryKey(targetScanId) });
          if (options?.projectId) {
            await queryCache.refetchAll({ prefix: getProjectActivityLogQueryKeyPrefix(options.projectId) });
          }
          return result as unknown as ApiScan;
        }),
    }), [kaiProApiClient, options?.projectId, queryCache]),
  };
};

export const useUser = (userId: string | undefined | null) => {
  const kaiProApiClient = useKaiProApiClient();

  const query = useCachedQuery(
    getUserByIdQueryKey(userId),
    async (key, queryCache) => kaiProApiClient.getUser(userId!),
    { enabled: !!kaiProApiClient.token && !!userId },
  );

  return {
    ...query,
    user: query.data,
  };
};
